import { Link, useNavigate } from 'react-router-dom';
import React, { useEffect } from 'react';
import { Mail, Lock, Loader2, ArrowRight } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    loginUser,
    clearError,
    selectIsLoading,
    selectAuthError,
    selectIsAuthenticated,
    selectRegistrationComplete,
    selectOrganisationProvisioned,
} from '../store/slices/authSlice';

const Login = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const isLoading = useAppSelector(selectIsLoading);
    const error = useAppSelector(selectAuthError);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const registrationComplete = useAppSelector(selectRegistrationComplete);
    const organisationProvisioned = useAppSelector(selectOrganisationProvisioned);

    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');

    // Navigate after successful login based on registration status
    useEffect(() => {
        if (isAuthenticated) {
            if (!registrationComplete) {
                navigate('/complete-profile', { replace: true });
            } else if (!organisationProvisioned) {
                navigate('/setup-organisation', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [isAuthenticated, registrationComplete, organisationProvisioned, navigate]);

    // Clear any stale errors when navigating to login
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading) return; // Prevent double-submit
        dispatch(loginUser({ email, password }));
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
        if (error) dispatch(clearError());
    };

    return (
        <div className="min-h-screen flex">
            {/* Left panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 via-green-700 to-teal-800 relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
                    <div className="absolute bottom-32 right-16 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-teal-300/10 rounded-full blur-2xl" />
                    {/* Grid pattern */}
                    <div className="absolute inset-0 opacity-[0.03]"
                         style={{
                             backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                             backgroundSize: '60px 60px'
                         }}
                    />
                </div>

                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                <span className="text-white font-display font-bold text-lg">W</span>
                            </div>
                            <span className="text-white font-display font-bold text-2xl tracking-tight">wadakaroo</span>
                        </div>
                    </div>

                    <div className="max-w-md">
                        <h1 className="text-4xl font-display font-bold text-white leading-tight mb-6">
                            Your business,<br />
                            <span className="text-emerald-200">organized & thriving.</span>
                        </h1>
                        <p className="text-emerald-100/80 text-lg leading-relaxed">
                            Streamline your operations, manage your team, and grow your
                            business — all in one platform built for modern organisations.
                        </p>
                    </div>

                    <div className="flex items-center gap-8 text-emerald-200/60 text-sm">
                        <span>© 2025 Wadakaroo</span>
                        <span>·</span>
                        <span>Privacy</span>
                        <span>·</span>
                        <span>Terms</span>
                    </div>
                </div>
            </div>

            {/* Right panel - Login form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-gray-50">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                            <span className="text-white font-display font-bold text-lg">W</span>
                        </div>
                        <span className="text-gray-900 font-display font-bold text-2xl tracking-tight">wadakaroo</span>
                    </div>

                    <div className="mb-8">
                        <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                            Welcome back
                        </h2>
                        <p className="text-gray-500">
                            Sign in to your account to continue
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Email address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={handleInputChange(setEmail)}
                                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                    placeholder="you@company.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <button
                                    type="button"
                                    onClick={() => navigate('/forgot-password')}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium bg-transparent border-none cursor-pointer"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={handleInputChange(setPassword)}
                                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                    placeholder="••••••••"
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                                isLoading
                                    ? 'bg-emerald-400 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
                            } text-white shadow-sm`}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500">
                            Don't have an account?{' '}
                            <Link
                                to="/register"
                                className="text-emerald-600 hover:text-emerald-700 font-semibold"
                            >
                                Create one free
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;